<template>
    <div v-if="subFormAvailable">
        <div>
            <b-row class="d-flex align-items-center">
                <h2>
                    Forderung
                </h2>
                <b-button
                    class="klippa-subform-toggle-btn"
                    variant="primary"
                    @click="toggleAll">
                    {{ someIncluded ? 'Alle abwählen' : 'Alle auswählen' }}
                </b-button>
            </b-row>
            <b-row>
                <b-col>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-12"
                            :default-value="NEW_COST_UNIT_SENTINEL"
                            :form-context="formContext"
                            label="Auswahl Forderung"
                            :options="initialCostUnitOptions"
                            :options-loader="loadCostUnitOptions"
                            property="costUnitId"
                            :subform-identifier="subformIdentifier"
                            type="autocomplete"
                            @change="onCostUnitSelected"/>
                    </b-row>
                    <b-row>
                        <csp-klippa-info-card-element
                            ref="fieldReceipt"
                            class="col-12"
                            :form-context="formContext"
                            label="Beleg Nr."
                            property="receipt"
                            :subform-identifier="subformIdentifier"
                            type="text"
                            @field-hover-changed="(data) => {$emit('field-hover-changed', data)}"
                            @field-inclusion-changed="onFieldInclusionChangedWithTracking"/>
                    </b-row>
                    <b-row>
                        <csp-klippa-info-card-element
                            ref="fieldAmount"
                            class="col-12"
                            :form-context="formContext"
                            label="Bruttobetrag"
                            property="amount"
                            :subform-identifier="subformIdentifier"
                            type="currency"
                            @field-hover-changed="(data) => {$emit('field-hover-changed', data)}"
                            @field-inclusion-changed="onFieldInclusionChangedWithTracking"/>
                    </b-row>
                    <b-row>
                        <csp-klippa-info-card-element
                            ref="fieldAmountNet"
                            class="col-12"
                            :form-context="formContext"
                            label="Nettobetrag"
                            property="amountNet"
                            :subform-identifier="subformIdentifier"
                            type="currency"
                            @field-hover-changed="(data) => {$emit('field-hover-changed', data)}"
                            @field-inclusion-changed="onFieldInclusionChangedWithTracking"/>
                    </b-row>
                    <b-row>
                        <csp-klippa-info-card-element
                            ref="fieldAmountVat"
                            class="col-12"
                            :form-context="formContext"
                            label="USt.-Betrag"
                            property="amountVat"
                            :subform-identifier="subformIdentifier"
                            type="currency"
                            @field-hover-changed="(data) => {$emit('field-hover-changed', data)}"
                            @field-inclusion-changed="onFieldInclusionChangedWithTracking"/>
                    </b-row>
                    <b-row v-if="copaymentAvailable">
                        <csp-klippa-info-card-element
                            ref="fieldAmountCopayment"
                            class="col-12"
                            :form-context="formContext"
                            label="Selbstbeteiligung"
                            property="amountCopayment"
                            :subform-identifier="subformIdentifier"
                            type="currency"
                            @field-hover-changed="(data) => {$emit('field-hover-changed', data)}"
                            @field-inclusion-changed="onFieldInclusionChangedWithTracking"/>
                    </b-row>
                    <b-row>
                        <csp-klippa-info-card-element
                            ref="fieldVatRate"
                            class="col-12"
                            :form-context="formContext"
                            label="USt. (%)"
                            :options-loader="loadVatRateOptions"
                            property="vatRate"
                            :subform-identifier="subformIdentifier"
                            type="autocomplete"
                            @field-hover-changed="(data) => {$emit('field-hover-changed', data)}"
                            @field-inclusion-changed="onFieldInclusionChangedWithTracking"/>
                    </b-row>
                    <b-row>
                        <csp-klippa-info-card-element
                            ref="fieldPaymentComment"
                            class="col-12"
                            :form-context="formContext"
                            label="Bemerkung"
                            property="paymentComment"
                            :subform-identifier="subformIdentifier"
                            type="textarea"
                            @field-hover-changed="(data) => {$emit('field-hover-changed', data)}"
                            @field-inclusion-changed="onFieldInclusionChangedWithTracking"/>
                    </b-row>
                </b-col>
            </b-row>
        </div>
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>
    </div>
</template>

<script>
import Container from '@services/Container';
import FormDataMixin from '@desktop/components/mixins/FormDataMixin';

const container = Container.getInstance();
const recordRepository = container.getDesktopRecordRepository();
const COST_UNIT_FIELDS = [
    'receipt',
    'amount',
    'amountNet',
    'amountVat',
    'amountCopayment',
    'vatRate',
    'paymentComment',
];

const FIELD_REFS = COST_UNIT_FIELDS.map((field) => 'field' + field.charAt(0).toUpperCase() + field.slice(1));

const NEW_COST_UNIT_SENTINEL = 0;

export default {
    name: 'DataApplicationCostUnitSubForm',
    mixins: [FormDataMixin],
    props: {
        fileId: {
            type: Number,
            required: true,
        },
        formContext: {
            type: Object,
            required: true,
        },
        recordId: {
            type: Number,
            required: true,
        },
    },
    emits: [
        'field-inclusion-changed',
        'field-hover-changed',
    ],
    data() {
        return {
            initialCostUnitOptions: [
                {value: NEW_COST_UNIT_SENTINEL, text: 'Neue Forderung erstellen'},
            ],
            fieldInclusionStates: {},
            NEW_COST_UNIT_SENTINEL,
            subformIdentifier: 'costUnit',
            isLoading: false,
        }
    },
    computed: {
        copaymentAvailable() {
            return this.formContext.data?.costUnit?.data?.attributes?.canHaveCostUnitCopayment ?? false;
        },
        someIncluded() {
            const values = Object.values(this.fieldInclusionStates);
            return values.length === 0 || values.some((v) => v);
        },
        subFormAvailable() {
            if (!this.subformIdentifier) {
                return false;
            }
            if (!Array.isArray(this.formContext.subformIdentifiers)) {
                return false;
            }
            return this.formContext.subformIdentifiers.indexOf(this.subformIdentifier) !== -1;
        },
    },
    methods: {
        applyDataBaseValues(fields) {
            const subformContextData = this.formContext.form.getContextData()[this.subformIdentifier];
            if (!subformContextData) {
                return;
            }
            COST_UNIT_FIELDS.forEach((field) => {
                if (subformContextData[field]?.contextData !== undefined) {
                    const dataBaseValue = fields[field]?.contextData?.dataBaseValue ?? null;
                    this.$set(subformContextData[field].contextData, 'dataBaseValue', dataBaseValue);
                }
            });
        },
        clearDataBaseValues() {
            const subformContextData = this.formContext.form.getContextData()[this.subformIdentifier];
            if (!subformContextData) {
                return;
            }
            COST_UNIT_FIELDS.forEach((field) => {
                if (subformContextData[field]?.contextData !== undefined) {
                    this.$set(subformContextData[field].contextData, 'dataBaseValue', null);
                }
            });
        },
        loadCostUnitOptions() {
            return recordRepository.loadKlippaCostUnitOptions(this.recordId, this.fileId)
                .then((options) => [
                    {value: NEW_COST_UNIT_SENTINEL, text: 'Neue Forderung erstellen'},
                    ...options,
                ]);
        },
        loadVatRateOptions() {
            return recordRepository.loadLegacySelectOptions(
                'vehicleDamageRecordCostUnitVatRates',
                {
                    vehicleDamageRecordId: this.recordId,
                    vehicleDamageRecordCostUnitCodeId: this.formContext
                        .complexFields[this.subformIdentifier]
                        .vatRate
                        .contextData
                        .costUnitCode ?? null,
                }
            );
        },
        onCostUnitSelected(costUnitId) {
            if (!costUnitId) {
                this.clearDataBaseValues();
                return;
            }
            this.isLoading = true;
            recordRepository.loadKlippaCostUnitData(this.recordId, this.fileId, costUnitId)
                .then((response) => {
                    const fields = response.data?.attributes?.fields ?? {};
                    this.applyDataBaseValues(fields);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        onFieldInclusionChangedWithTracking(data) {
            this.$set(this.fieldInclusionStates, data.property, data.included);
            this.$emit('field-inclusion-changed', data);
        },
        toggleAll() {
            const target = !this.someIncluded;
            FIELD_REFS.forEach((refName) => {
                const ref = this.$refs[refName];
                if (ref) {
                    ref.setIncluded(target);
                }
            });
        },
    },
}
</script>
